import * as React from 'react';

import { MutationStatus } from '@tanstack/react-query';
import { Box, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Save as SaveIcon } from '@mui/icons-material';

import { DateTime } from 'luxon';

import useInterval from '../core/api/hooks/common/useInterval';

export interface SaveButtonProps {
  onSave?: () => void;
  isDirty?: boolean;
  saveStatus?: MutationStatus;
  lastSaved?: string | null;
  enableAutoSave?: boolean;
  isHidden?: boolean
}

function SaveButton({
  onSave,
  isDirty,
  saveStatus,
  lastSaved,
  enableAutoSave,
  isHidden,
}: SaveButtonProps): JSX.Element {
  const [lastSavedRelative, setLastSavedRelative] = React.useState<string | null>(null);

  const setRelative = (time: string) => {
    if (lastSaved) {
      setLastSavedRelative(DateTime.fromISO(time).toRelative());
    }
  };

  React.useEffect(() => {
    if (lastSaved) {
      setRelative(lastSaved);
    }
  }, [lastSaved]);

  useInterval(() => {
    if (lastSaved) {
      setRelative(lastSaved);
    }
  }, 5000);

  useInterval(() => {
    if (enableAutoSave && isDirty && onSave) {
      console.debug('Autosave :: Starting');
      onSave();
    }
  }, 120000);

  const getSaveButtonText = () => {
    if (saveStatus === 'pending') return 'Saving';
    if (saveStatus === 'success' && !isDirty) return 'Saved';

    return 'Save';
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" sx={{ pt: '18px' }}>
      { !isHidden && (
      <React.Fragment>
        <LoadingButton
          size="small"
          color="primary"
          disabled={saveStatus === 'pending' || (saveStatus === 'success' && !isDirty) || saveStatus === 'error'}
          variant="contained"
          endIcon={<SaveIcon />}
          loadingPosition="end"
          loading={saveStatus === 'pending'}
          onClick={onSave}
        >
          {getSaveButtonText()}
        </LoadingButton>
        <Typography variant="caption">
          {lastSavedRelative !== null ? lastSavedRelative : 'Unsaved'}
        </Typography>
      </React.Fragment>
      )}
    </Box>
  );
}

export default SaveButton;
